import {get} from "lodash"
import React, {FC, useEffect, useState} from "react"

import withAuth, {WithAuth} from "@irati/auth/helpers/auth/withAuth"
import RedemptionList, {getRedemptionList, removeFromRedemptionList} from "@irati/checkout/apollo/query/RedemptionList"
import redemptionsService from "@irati/checkout/services/redemptions"
import {CheckoutRedemption, CheckoutRedemptionType} from "@irati/types"
import {Hr} from "@irati/ui/src/Layout"
import {Body} from "@irati/ui/src/Typography"
import {getQuery} from "@irati/utils/history"
import {FormattedMessage, InjectIntl, injectIntl} from "@irati/utils/lang"
import notify from "@irati/utils/notify"

import styled, {css} from "styled-components"
import ShowMoreOrThatsAll, {onNewPage} from "../../DealList/ShowMoreOrThatsAll"
import MarkPerkAsUsedPopup from "../MarkPerkAsUsedPopup"
import MyPerksItem from "../MyPerksItem"
import NoPerks from "../NoPerks"
import {SummaryPopup} from "../SummaryPopup"
import RedemptionHeaders from "./Headers"

interface Props extends InjectIntl, WithAuth {
  isUsedPerks?: boolean
}

const DesktopViewWrapper = styled.div`
  ${({theme}) => css`
    ${theme.mediaQueries.maxWidthMedia.desktop`
      display: none;
    `}
  `}
`

const PerksList: FC<Props> = ({formatMessage, isUsedPerks = false, auth}) => {
  const [showRedemptionPopup, setShowRedemptionPopup] = useState(false)
  const [selectedRedemption, setSelectedRedemption] = useState<CheckoutRedemption | null>(null)

  useEffect(() => {
    const redemptionUuid = get(getQuery(), "redemptionUuid", null)
    if (redemptionUuid) {
      redemptionsService.get(redemptionUuid).then((redemption: CheckoutRedemption) => {
        viewRedemption(redemption)
      })
    }
  }, [])

  const variables = {
    isUsed: isUsedPerks,
    userId: auth.user.uuid,
    types: [CheckoutRedemptionType.Perks, CheckoutRedemptionType.FlexiReward, CheckoutRedemptionType.Merchandise]
  }

  const viewRedemption = (redemption: CheckoutRedemption) => {
    setShowRedemptionPopup(true)
    setSelectedRedemption(redemption)
  }

  const handleMarkAsUsedPopup = (redemption: CheckoutRedemption) => {
    if (redemption.isUsed) {
      handleToggleIsUsed(redemption)
    } else {
      setSelectedRedemption(redemption)
    }
  }

  const handleClose = () => {
    setSelectedRedemption(null)
    setShowRedemptionPopup(false)
  }

  const handleToggleIsUsed = (redemption: CheckoutRedemption) => {

    removeFromRedemptionList(redemption, variables)
    return redemptionsService.toggleIsUsed(redemption).then(() => {
      const message = !redemption.isUsed
        ? "perks.myPerks.markPerkAsUsed.marked.success.message"
        : "perks.myPerks.markPerkAsUsed.unmarked.success.message"

      notify.success({
        message: formatMessage({id: message}, {header: get(redemption, "dealSnapshot.header")}),
        position: "top-right"
      })
      setSelectedRedemption(null)
    })
  }

  const handleArchiveSuccess = (redemption: CheckoutRedemption) => {

    notify.success({
      message: formatMessage({id: "perks.myPerks.archive.success.message"}, {header: get(redemption, "dealSnapshot.header")}),
      position: "top-right"
    })
    removeFromRedemptionList(redemption, variables)
  }

  const handleLoadMore: onNewPage = (isVisible, apollo) => {
    if (isVisible) {
      const {hasMore, cursor} = getRedemptionList(apollo)
      const shouldFetchMore = !apollo.loading && hasMore && cursor
      if (shouldFetchMore) {
        return apollo.nextPage({...variables, cursor})
      }
    }
  }

  return (
    <>
      <>
        <DesktopViewWrapper>
          <Body marginBottom={4} marginTop={6}>
            <FormattedMessage id={isUsedPerks ? "perks.myPerks.myPerksPage.usedSubHeader" : "perks.myPerks.myPerksPage.newSubHeader"} />
          </Body>
          <Hr />
        </DesktopViewWrapper>
        <RedemptionList variables={variables} useLoader={false} fetchPolicy="cache-and-network">
          {(apollo) => {
            const {results} = getRedemptionList(apollo)

            if (results.length === 0 && !apollo.loading) {
              return <NoPerks usedPerksOptionActive={isUsedPerks} />
            }

            return (
              <>
                <DesktopViewWrapper>
                  <RedemptionHeaders />
                </DesktopViewWrapper>
                {results.map((redemption, index) => (
                  <MyPerksItem
                    withHr={index !== 0}
                    key={redemption.uuid}
                    redemption={redemption}
                    onViewRedemption={() => viewRedemption(redemption)}
                    onMarkAsUsed={() => handleMarkAsUsedPopup(redemption)}
                    onArchiveSuccess={() => handleArchiveSuccess(redemption)}
                    usedPerksOptionActive={isUsedPerks} />
                ))}
                <ShowMoreOrThatsAll apollo={apollo} getList={getRedemptionList} onNewPage={handleLoadMore} />
              </>
            )
          }}
        </RedemptionList>
      </>
      {<SummaryPopup show={!!(selectedRedemption && showRedemptionPopup)} onHide={handleClose} redemption={selectedRedemption}  />}
      {selectedRedemption && !showRedemptionPopup && (
        <MarkPerkAsUsedPopup show onHide={handleClose} redemption={selectedRedemption} onSubmit={handleToggleIsUsed} />
      )}
    </>
  )
}


export default injectIntl(withAuth(PerksList))
